import { useEffect } from "react";
import { useLocation } from "react-router-dom";

type RouteMeta = {
  title: string;
  description: string;
  keywords?: string;
};

const SITE_DOMAIN = "https://www.venuevine.co.uk";

const routeMeta: Record<string, RouteMeta> = {
  "/": {
    title: "Corporate Venue Sourcing London | Venue Vine",
    description:
      "Venue Vine is a specialist corporate venue sourcing and intelligence partner for commercially driven teams across London and the UK. Expert briefing, independent recommendations, and intelligent negotiation — tell us about your event.",
    keywords:
      "corporate venue sourcing London, venue finding service London, corporate event venues London, leadership retreat venues UK, conference venue sourcing London, venue consultancy London",
  },
  "/services": {
    title: "Corporate Venue Sourcing Services | Venue Vine London",
    description:
      "Structured venue sourcing for corporate teams. We source, shortlist, and negotiate venues for meetings, conferences and corporate events.",
    keywords:
      "venue sourcing services, corporate venue consultancy, venue negotiation London, event venue shortlist",
  },
  "/faqs": {
    title: "Venue Sourcing FAQs | Venue Vine London",
    description:
      "Frequently asked questions about Venue Vine's venue sourcing services, process and pricing.",
    keywords:
      "venue sourcing FAQ, corporate venue questions, venue finder process",
  },
  "/blog": {
    title: "Venue Finding Insights London | Venue Vine",
    description:
      "Market insights, venue intelligence and corporate event planning guidance from Venue Vine London.",
    keywords:
      "venue insights London, corporate event blog, venue intelligence, event planning guidance",
  },
  "/insight": {
    title: "Commercial Insight Summary | Venue Vine London",
    description:
      "Review Venue Vine's commercial insight approach for evaluating venue value, negotiation opportunities, and event decision-making.",
    keywords:
      "venue commercial insight, venue negotiation strategy, event decision-making",
  },
  "/intelligence": {
    title: "Venue Finding Insights London | Venue Vine",
    description:
      "Market insights, venue intelligence and corporate event planning guidance from Venue Vine London.",
    keywords:
      "venue intelligence report, London corporate venues, venue market analysis",
  },
  "/partner/onboarding": {
    title: "Partner Onboarding | Venue Vine",
    description:
      "Onboard your venue with Venue Vine to be considered for curated corporate event opportunities in London.",
  },
};

const ensureMeta = (selector: string, attributes: Record<string, string>) => {
  let element = document.head.querySelector(selector) as HTMLMetaElement | null;

  if (!element) {
    element = document.createElement("meta");
    Object.entries(attributes).forEach(([key, value]) => element?.setAttribute(key, value));
    document.head.appendChild(element);
  }

  return element;
};

const SeoManager = () => {
  const location = useLocation();

  useEffect(() => {
    const meta = routeMeta[location.pathname] ?? routeMeta["/"];
    const canonicalUrl = location.pathname === "/" ? `${SITE_DOMAIN}/` : `${SITE_DOMAIN}${location.pathname}`;

    document.title = meta.title;

    const descriptionTag = ensureMeta('meta[name="description"]', { name: "description" });
    descriptionTag.setAttribute("content", meta.description);

    const robotsTag = ensureMeta('meta[name="robots"]', { name: "robots" });
    robotsTag.setAttribute("content", "index, follow, max-image-preview:large");

    if (meta.keywords) {
      const keywordsTag = ensureMeta('meta[name="keywords"]', { name: "keywords" });
      keywordsTag.setAttribute("content", meta.keywords);
    }

    const ogTitle = ensureMeta('meta[property="og:title"]', { property: "og:title" });
    ogTitle.setAttribute("content", meta.title);

    const ogDescription = ensureMeta('meta[property="og:description"]', { property: "og:description" });
    ogDescription.setAttribute("content", meta.description);

    const ogUrl = ensureMeta('meta[property="og:url"]', { property: "og:url" });
    ogUrl.setAttribute("content", canonicalUrl);

    const twitterTitle = ensureMeta('meta[name="twitter:title"]', { name: "twitter:title" });
    twitterTitle.setAttribute("content", meta.title);

    const twitterDescription = ensureMeta('meta[name="twitter:description"]', { name: "twitter:description" });
    twitterDescription.setAttribute("content", meta.description);

    let canonicalLink = document.head.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canonicalLink) {
      canonicalLink = document.createElement("link");
      canonicalLink.setAttribute("rel", "canonical");
      document.head.appendChild(canonicalLink);
    }
    canonicalLink.setAttribute("href", canonicalUrl);

    let structuredDataScript = document.getElementById("seo-jsonld") as HTMLScriptElement | null;
    if (!structuredDataScript) {
      structuredDataScript = document.createElement("script");
      structuredDataScript.id = "seo-jsonld";
      structuredDataScript.type = "application/ld+json";
      document.head.appendChild(structuredDataScript);
    }

    structuredDataScript.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "Organization",
          name: "Venue Vine",
          url: SITE_DOMAIN,
          description: "Corporate venue sourcing and event consultancy in London, United Kingdom.",
          email: "hello@venuevine.co.uk",
          address: {
            "@type": "PostalAddress",
            addressLocality: "London",
            addressCountry: "GB",
          },
          sameAs: [
            "https://instagram.com/venuevinelondon",
          ],
        },
        {
          "@type": "WebSite",
          name: "Venue Vine",
          url: SITE_DOMAIN,
        },
        {
          "@type": "WebPage",
          name: meta.title,
          description: meta.description,
          url: canonicalUrl,
          isPartOf: {
            "@type": "WebSite",
            name: "Venue Vine",
            url: SITE_DOMAIN,
          },
        },
        {
          "@type": "LocalBusiness",
          name: "Venue Vine",
          description: "Corporate venue sourcing and event consultancy.",
          url: SITE_DOMAIN,
          email: "hello@venuevine.co.uk",
          address: {
            "@type": "PostalAddress",
            addressLocality: "London",
            addressCountry: "GB",
          },
          priceRange: "££",
          areaServed: {
            "@type": "Place",
            name: "London, United Kingdom",
          },
        },
      ],
    });
  }, [location.pathname]);

  return null;
};

export default SeoManager;
